// src/pages/SOPPage.tsx
// Internal Standard Operating Procedures — admin-only.
// Contains: third-party carrier integration guide, driver onboarding, go-live checklist.
import React, { useState } from 'react';

interface SopSection {
  id: string;
  title: string;
  icon: string;
  content: React.ReactNode;
}

export default function SOPPage() {
  const [active, setActive] = useState('integration');

  const sections: SopSection[] = [
    {
      id: 'integration',
      title: 'Third-Party Carrier Integration',
      icon: '🔗',
      content: <IntegrationGuide />,
    },
    {
      id: 'onboarding',
      title: 'Driver Onboarding',
      icon: '🚐',
      content: <DriverOnboarding />,
    },
    {
      id: 'golive',
      title: 'Go-Live Checklist',
      icon: '✅',
      content: <GoLiveChecklist />,
    },
    {
      id: 'contact',
      title: 'Enterprise Sales Process',
      icon: '🤝',
      content: <SalesProcess />,
    },
  ];

  const current = sections.find(s => s.id === active)!;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Standard Operating Procedures</h1>
        <p style={{ color: 'var(--color-text-dim)', marginTop: '0.25rem', fontSize: '0.9rem' }}>
          Internal documentation — admin only. Not visible to customers or drivers.
        </p>
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
        {/* Left nav */}
        <div style={{ width: '220px', flexShrink: 0 }}>
          <div className="card" style={{ padding: '0.5rem' }}>
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  width: '100%', padding: '0.7rem 0.75rem',
                  background: active === s.id ? 'var(--color-accent)' : 'transparent',
                  color: active === s.id ? '#fff' : 'var(--color-text)',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: active === s.id ? '600' : '400',
                  textAlign: 'left',
                }}
              >
                <span>{s.icon}</span>
                {s.title}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="card">
            <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.2rem', fontWeight: 700 }}>
              {current.icon} {current.title}
            </h2>
            {current.content}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Third-Party Carrier Integration ─────────────────────────────────

function IntegrationGuide() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <SopBlock title="Overview">
        <p>MJ Maps connects to third-party carriers (DHL, DPD, Evri, Royal Mail, 3PLs, etc.) via an inbound orders API. The carrier pushes their daily manifest to us; we geocode, optimise, and assign stops to drivers automatically.</p>
        <p>Each integration requires: (1) an API key scoped to a dispatcher account, (2) a manifest endpoint or webhook, and (3) a driver assignment rule.</p>
      </SopBlock>

      <SopBlock title="Step 1 — Create a Dispatcher Account for the Partner">
        <ol>
          <li>Log into the admin portal and navigate to <strong>Users</strong>.</li>
          <li>Create a new account with role <code>dispatcher</code> and plan <code>enterprise</code>.</li>
          <li>Set the company name field to the carrier's trading name (e.g. <em>DHL Express UK</em>).</li>
          <li>Navigate to <strong>Users → [new account] → API Keys</strong> and generate an API key with scope <code>inbound_orders:write</code>.</li>
          <li>Record the API key securely — it will only be shown once. Hand it to the partner's integration team over an encrypted channel (not email).</li>
        </ol>
      </SopBlock>

      <SopBlock title="Step 2 — Manifest Format (POST /api/v1/orders)">
        <p>The partner pushes a manifest to this endpoint. Each order becomes one stop in MJ Maps. Authentication: <code>Authorization: Bearer &lt;api_key&gt;</code>.</p>
        <CodeBlock>{`POST /api/v1/orders
Content-Type: application/json

{
  "orders": [
    {
      "ref":          "DLVRY-001",          // carrier's reference (string, unique per day)
      "address":      "14 Oak Street, Bristol BS1 4PP",
      "name":         "Jane Smith",          // recipient name (optional)
      "phone":        "+447700900123",       // recipient phone for ETA SMS (optional)
      "email":        "j.smith@example.com", // recipient email (optional)
      "notes":        "Leave in porch",      // access notes (optional)
      "parcel_count": 2,                     // default 1
      "time_window_start": "09:00",          // delivery window (optional, HH:MM)
      "time_window_end":   "17:00"
    }
  ],
  "route_date":  "2025-07-10",           // ISO date, defaults to today
  "driver_id":   "uuid-of-assigned-driver" // optional — auto-assigns if omitted
}`}
        </CodeBlock>
        <p><strong>Response:</strong></p>
        <CodeBlock>{`{
  "ok": true,
  "routeId":   "uuid-of-created-route",
  "stopCount": 1,
  "warnings":  []  // geocode failures, unresolved addresses
}`}
        </CodeBlock>
      </SopBlock>

      <SopBlock title="Step 3 — Webhook Push (Alternative to Polling)">
        <p>If the carrier prefers push rather than our pulling their manifest, configure a webhook on their side to POST to our inbound orders endpoint at a scheduled time (typically 05:00–06:00 local).</p>
        <p>For carriers that use SFTP manifest files, set up the <strong>manifest importer cron job</strong> on Railway:</p>
        <CodeBlock>{`# Railway cron — add via Railway dashboard → Service → Cron Jobs
0 5 * * 1-6  node scripts/import-sftp-manifest.js --carrier=dhl --env=production`}
        </CodeBlock>
        <p>The SFTP credentials go in Railway environment variables: <code>SFTP_DHL_HOST</code>, <code>SFTP_DHL_USER</code>, <code>SFTP_DHL_KEY</code>.</p>
      </SopBlock>

      <SopBlock title="Step 4 — Driver Assignment Rules">
        <p>Routes are assigned to drivers by the dispatcher. Three modes:</p>
        <ol>
          <li><strong>Manual assignment</strong> — dispatcher drags routes to drivers in the web dashboard.</li>
          <li><strong>Auto-assign by postcode district</strong> — configure in <code>dispatcher_settings</code> table: <code>{"{ \"auto_assign\": { \"SW1\": \"driver-uuid\", \"SW2\": \"driver-uuid\" } }"}</code></li>
          <li><strong>API-driven</strong> — carrier passes <code>driver_id</code> in the manifest (requires the carrier to maintain a driver ID mapping).</li>
        </ol>
      </SopBlock>

      <SopBlock title="Step 5 — Re-optimization on Driver Login">
        <p>Stops pushed via the inbound orders API are held as <code>status = 'pending'</code>. When the assigned driver taps <strong>Start Shift</strong>, the driver app calls:</p>
        <CodeBlock>{`GET /api/v1/routes/assigned-today`}
        </CodeBlock>
        <p>This fetches all pending stops for that driver's route and the shift-start screen presents them for review. The driver then triggers <code>POST /api/v1/routes/optimise</code>, which runs a <em>fresh</em> optimisation at that moment. This ensures any last-minute stop additions by the carrier are included in the final route order.</p>
        <p><strong>Late additions:</strong> If the carrier pushes additional stops after the driver has started their shift, the dispatcher can trigger a re-plan from their dashboard. The driver receives an in-app alert and can accept or defer the re-plan.</p>
      </SopBlock>

      <SopBlock title="Environment Variables Required">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Variable</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Purpose</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['JWT_SECRET', 'Token signing — must be ≥32 char random string'],
              ['DATABASE_URL', 'PostgreSQL connection string'],
              ['REDIS_URL', 'Redis connection string'],
              ['WHAT3WORDS_API_KEY', 'Precise destination geocoding'],
              ['VALHALLA_URL', 'Turn-by-turn routing engine (Stadia Maps)'],
              ['STADIA_API_KEY', 'Stadia Maps authentication'],
              ['OSRM_URL', 'Matrix routing (internal Railway service)'],
              ['DFT_STREET_MANAGER_KEY', 'UK roadworks data'],
              ['TELEGRAM_BOT_TOKEN', 'Dispatcher alert notifications'],
              ['TELEGRAM_DISPATCHER_CHAT_ID', 'Chat ID for dispatcher alerts'],
              ['FCM_SERVER_KEY', 'Push notifications to driver app'],
            ].map(([k, v]) => (
              <tr key={k} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem' }}><code>{k}</code></td>
                <td style={{ padding: '0.5rem', color: 'var(--color-text-dim)' }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SopBlock>
    </div>
  );
}

// ─── Section: Driver Onboarding ───────────────────────────────────────────────

function DriverOnboarding() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <SopBlock title="Overview">
        <p>Onboarding a new driver takes approximately 10 minutes end-to-end. Each driver needs: (1) an MJ Maps account, (2) the driver app installed, (3) a vehicle profile configured, and (4) at least one test delivery completed before going live.</p>
      </SopBlock>

      <SopBlock title="Step 1 — Account Creation">
        <ol>
          <li>Driver downloads MJ Maps from the App Store or Google Play, or navigates to <code>mjmapsystems.com/driver</code> on mobile web.</li>
          <li>Driver registers with their work email address. If the employer has a VIP invite code, the driver enters it to get the correct plan assigned automatically.</li>
          <li>Admin confirms the account in the portal: <strong>Users → [driver email] → Plan: Pro or Enterprise</strong>.</li>
          <li>If the business is paying centrally, set the driver's subscription to <code>managed</code> so no personal card is required.</li>
        </ol>
      </SopBlock>

      <SopBlock title="Step 2 — Vehicle Profile">
        <ol>
          <li>Driver opens the app and navigates to <strong>Vehicle</strong> (tap the vehicle chip on the Shift Start screen).</li>
          <li>Select vehicle type from the list. For HGVs, select the closest profile (e.g. <em>18t rigid</em> or <em>44t artic</em>).</li>
          <li>If the vehicle has non-standard dimensions (e.g. a custom-built curtainsider), tap <strong>Custom dimensions</strong> and enter exact height, width, and length in metres.</li>
          <li>The vehicle profile is used by the navigation guard to detect low bridges, weight restrictions, and access restrictions in real time.</li>
        </ol>
      </SopBlock>

      <SopBlock title="Step 3 — First Delivery Walkthrough">
        <ol>
          <li>On the Shift Start screen, tap <strong>Plan My Route</strong> and add 2–3 test addresses near the depot.</li>
          <li>Tap <strong>Start Shift</strong> — the app will request background location permission. This is required and must be granted.</li>
          <li>Review the optimised route order. Tap <strong>Confirm &amp; Start</strong>.</li>
          <li>Navigate to the first stop using the in-app navigation. Practise tapping <strong>Delivered</strong> and completing the post-delivery flow (difficulty report, local knowledge sheet).</li>
          <li>At the end of the test shift, confirm the scorecard appears correctly.</li>
        </ol>
      </SopBlock>

      <SopBlock title="Step 4 — Dispatcher Route Assignment (Enterprise)">
        <p>For enterprise deployments where the dispatcher pre-builds routes:</p>
        <ol>
          <li>The dispatcher imports or creates the route in the dispatcher portal before the driver's shift starts.</li>
          <li>When the driver opens the app and taps <strong>Shift Start</strong>, a blue banner will appear: <em>"Dispatcher has queued X stops for you today"</em>.</li>
          <li>Driver taps <strong>Load Dispatcher Route</strong> to pull in those stops — this performs a fresh optimisation at that moment, picking up any last-minute additions.</li>
          <li>Driver reviews the route and confirms. They do not need to manually enter any addresses.</li>
        </ol>
      </SopBlock>

      <SopBlock title="Troubleshooting">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Issue</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Resolution</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Driver can\'t log in', 'Check account is active in Users page. Check plan has not expired.'],
              ['Route not appearing', 'Confirm dispatcher assigned the route to the correct driver UUID.'],
              ['Navigation not working', 'Ensure VALHALLA_URL env var is set and Stadia Maps key is valid.'],
              ['No background location', 'Driver must grant Always Allow in iOS Settings → Privacy → Location Services → MJ Maps.'],
              ['Push notifications missing', 'Check FCM_SERVER_KEY. Verify driver\'s FCM token is registered (Users → driver → FCM tokens).'],
              ['Delivery PIN not trusted', 'PIN requires 5 distinct driver confirmations before isTrusted = true.'],
            ].map(([issue, fix]) => (
              <tr key={issue} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem', fontWeight: '600' }}>{issue}</td>
                <td style={{ padding: '0.5rem', color: 'var(--color-text-dim)' }}>{fix}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SopBlock>
    </div>
  );
}

// ─── Section: Go-Live Checklist ───────────────────────────────────────────────

function GoLiveChecklist() {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setChecked(p => ({ ...p, [id]: !p[id] }));

  const groups: { heading: string; items: { id: string; label: string; note?: string }[] }[] = [
    {
      heading: 'Infrastructure',
      items: [
        { id: 'db', label: 'PostgreSQL with PostGIS extension provisioned', note: 'Run all migrations in order: 000 → 057' },
        { id: 'redis', label: 'Redis provisioned and REDIS_URL set' },
        { id: 'jwt', label: 'JWT_SECRET set to ≥32 char random string (not default)' },
        { id: 'railway', label: 'All Railway services deployed and health checks passing' },
        { id: 'domain', label: 'Custom domain configured with SSL (mjmapsystems.com)' },
      ],
    },
    {
      heading: 'API Keys & Third-Party Services',
      items: [
        { id: 'stadia', label: 'STADIA_API_KEY set and VALHALLA_URL pointing to Stadia Valhalla' },
        { id: 'w3w', label: 'WHAT3WORDS_API_KEY set and usage limits checked' },
        { id: 'telegram', label: 'Telegram bot created, TELEGRAM_BOT_TOKEN and TELEGRAM_DISPATCHER_CHAT_ID set' },
        { id: 'fcm', label: 'FCM project created, FCM_SERVER_KEY set' },
        { id: 'stripe', label: 'Stripe live keys set (STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET)' },
        { id: 'dft', label: 'DFT Street Manager key set for UK roadworks data (optional but recommended)' },
      ],
    },
    {
      heading: 'Admin Portal',
      items: [
        { id: 'admin_setup', label: 'Admin account created via POST /api/v1/admin/setup' },
        { id: 'admin_login', label: 'Admin portal login verified at admin.mjmapsystems.com' },
        { id: 'feature_flags', label: 'Feature flags reviewed — NAVIGATION, ROUTE_OPTIMISE, DISPATCHER enabled as appropriate' },
        { id: 'audit', label: 'Audit log populated from setup actions (verify at /audit)' },
      ],
    },
    {
      heading: 'Driver App',
      items: [
        { id: 'app_published', label: 'Driver app published to App Store and Google Play' },
        { id: 'expo_url', label: 'EXPO_PUBLIC_API_URL set to production API base' },
        { id: 'test_login', label: 'Test driver account created and login verified' },
        { id: 'test_route', label: 'Test delivery completed end-to-end (optimise → navigate → complete)' },
        { id: 'test_pin', label: 'Delivery PIN drop tested and confirmed pin appears in address_pins table' },
        { id: 'test_notif', label: 'Push notification received on test device' },
      ],
    },
    {
      heading: 'Enterprise Partner (if applicable)',
      items: [
        { id: 'partner_account', label: 'Dispatcher account created for partner with correct plan' },
        { id: 'partner_apikey', label: 'API key generated and delivered to partner integration team securely' },
        { id: 'partner_test', label: 'Test manifest pushed via POST /api/v1/orders and stops visible in DB' },
        { id: 'partner_assign', label: 'Test route assigned to driver — driver app shows dispatcher banner' },
        { id: 'partner_fresh', label: 'Fresh optimisation on login verified — sequence_numbers updated after Start Shift' },
      ],
    },
  ];

  const total = groups.flatMap(g => g.items).length;
  const done  = Object.values(checked).filter(Boolean).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ flex: 1, height: '8px', background: 'var(--color-border)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: `${(done / total) * 100}%`, height: '100%', background: 'var(--color-accent)', transition: 'width 0.2s' }} />
        </div>
        <span style={{ fontSize: '0.85rem', color: 'var(--color-text-dim)', flexShrink: 0 }}>{done} / {total}</span>
      </div>

      {groups.map(group => (
        <div key={group.heading}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-dim)', marginBottom: '0.6rem' }}>
            {group.heading}
          </div>
          {group.items.map(item => (
            <label key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.55rem 0', cursor: 'pointer', borderBottom: '1px solid var(--color-border)' }}>
              <input
                type="checkbox"
                checked={!!checked[item.id]}
                onChange={() => toggle(item.id)}
                style={{ marginTop: '3px', accentColor: 'var(--color-accent)', width: '16px', height: '16px', flexShrink: 0 }}
              />
              <div>
                <span style={{ fontSize: '0.9rem', textDecoration: checked[item.id] ? 'line-through' : 'none', color: checked[item.id] ? 'var(--color-text-dim)' : 'var(--color-text)' }}>
                  {item.label}
                </span>
                {item.note && <div style={{ fontSize: '0.78rem', color: 'var(--color-text-dim)', marginTop: '2px' }}>{item.note}</div>}
              </div>
            </label>
          ))}
        </div>
      ))}

      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-dim)', marginTop: '0.5rem' }}>
        Check state is saved in browser session only — reset when you reload.
      </p>
    </div>
  );
}

// ─── Section: Enterprise Sales Process ───────────────────────────────────────

function SalesProcess() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <SopBlock title="Overview">
        <p>Enterprise sales require a consultative approach. The typical cycle is: <strong>initial enquiry → discovery call → pilot deployment → commercial agreement → full rollout</strong>. Budget 4–8 weeks for the full cycle.</p>
        <p>All enterprise enquiries currently come via <code>hello@mjmaps.co.uk</code> or in-person through the owner's network. The enterprise portal in the driver app shows <em>Coming Soon</em> and directs prospects to email until the enterprise portal is built and ready.</p>
      </SopBlock>

      <SopBlock title="Discovery Call Template">
        <p>Cover these points in the initial call:</p>
        <ol>
          <li><strong>Fleet size</strong> — number of active drivers, number of daily deliveries</li>
          <li><strong>Depot structure</strong> — single depot or multi-depot? UK only or cross-border?</li>
          <li><strong>Current system</strong> — what are they using now? (paper, Paragon, Circuit, etc.)</li>
          <li><strong>Integration requirements</strong> — does their WMS/TMS push manifests? What format?</li>
          <li><strong>Vehicle types</strong> — car, SWB van, LWB van, 3.5t, 7.5t, HGV, artic?</li>
          <li><strong>POD requirements</strong> — photo, barcode scan, signature, or all three?</li>
          <li><strong>Timeline</strong> — when do they need to go live?</li>
          <li><strong>Budget indicator</strong> — per-driver monthly vs. flat fleet fee?</li>
        </ol>
      </SopBlock>

      <SopBlock title="Pilot Deployment Process">
        <ol>
          <li>Agree on a pilot fleet (typically 3–10 drivers for 2 weeks).</li>
          <li>Create a staging environment or use production with enterprise-tier accounts.</li>
          <li>Integrate with their manifest source (SFTP, API, or manual CSV upload).</li>
          <li>Run the go-live checklist (see <em>Go-Live Checklist</em> tab) for the pilot.</li>
          <li>Daily check-ins during the first week; weekly during week 2.</li>
          <li>Collect driver feedback via the difficulty report and note data.</li>
          <li>Present pilot outcomes: DIFOT improvement, time saved, delivery accuracy.</li>
        </ol>
      </SopBlock>

      <SopBlock title="Commercial Structure (Reference)">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Fleet Size</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Indicative Price</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['2–10 drivers', '£9.99/driver/mo', 'Pro plan per driver — self-serve'],
              ['11–50 drivers', 'From £7.99/driver/mo', 'Enterprise — volume discount, dispatcher portal'],
              ['51–200 drivers', 'From £5.99/driver/mo', 'Enterprise — dedicated support, SLA, custom branding'],
              ['200+ drivers', 'Custom', 'White-label option, on-premise option negotiable'],
            ].map(([size, price, notes]) => (
              <tr key={size} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem', fontWeight: '600' }}>{size}</td>
                <td style={{ padding: '0.5rem' }}>{price}</td>
                <td style={{ padding: '0.5rem', color: 'var(--color-text-dim)' }}>{notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-dim)', marginTop: '0.75rem' }}>
          Indicative only — adjust based on requirements, integrations, and support level agreed. Do not share this table externally without owner approval.
        </p>
      </SopBlock>

      <SopBlock title="Handover to Technical Implementation">
        <p>Once a commercial agreement is signed, the technical implementation follows the <em>Third-Party Carrier Integration</em> guide. Key handover artefacts:</p>
        <ol>
          <li>Completed Discovery Call notes</li>
          <li>Fleet size, depot count, vehicle types</li>
          <li>Manifest format specification (CSV columns, API schema, or SFTP path)</li>
          <li>List of drivers to onboard (name, email, vehicle type)</li>
          <li>Go-live date agreed</li>
          <li>Named point of contact on client side for technical queries</li>
        </ol>
      </SopBlock>
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────────────────

function SopBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 style={{ margin: '0 0 0.6rem', fontSize: '0.95rem', fontWeight: 700, color: 'var(--color-text)' }}>{title}</h3>
      <div style={{ fontSize: '0.88rem', lineHeight: '1.6', color: 'var(--color-text-dim)', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {children}
      </div>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre style={{
      background: 'var(--color-bg)',
      border: '1px solid var(--color-border)',
      borderRadius: '6px',
      padding: '0.75rem 1rem',
      overflowX: 'auto',
      fontSize: '0.78rem',
      lineHeight: '1.5',
      color: 'var(--color-text)',
      margin: '0.4rem 0',
    }}>
      <code>{children}</code>
    </pre>
  );
}
